import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { DateFormatPipe } from '../../../../../shared/pipes';
import { ChemicalTemplateService } from '../../../../../core/data-services/chemical-template.service';
import { ChemicalTemplate } from '../../../../../core/data-models/chemical-template.model';

// Using the ChemicalTemplate interface from the service

@Component({
  selector: 'app-chemical-template-details',
  standalone: true,
  imports: [CommonModule, RouterLink, ConfirmModalComponent, DateFormatPipe],
  templateUrl: './chemical-template-details.component.html',
  styleUrls: ['./chemical-template-details.component.scss']
})
export class ChemicalTemplateDetailsComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  private chemicalTemplateService = inject(ChemicalTemplateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  // State
  isLoading = true;
  errorMessage: string | null = null;
  templateId: string | null = null;
  template: ChemicalTemplate | null = null;

  ngOnInit(): void {
    this.templateId = this.route.snapshot.paramMap.get('id');
    if (this.templateId) {
      this.loadTemplate(this.templateId);
    } else {
      this.errorMessage = 'No template ID provided';
      this.isLoading = false;
    }
  }

  private async loadTemplate(id: string): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;

    try {
      const template = await this.chemicalTemplateService.getTemplateById(id);
      if (template) {
        this.template = template;
      } else {
        this.errorMessage = 'Template not found';
      }
    } catch (error) {
      console.error('Error loading template:', error);
      this.errorMessage = 'Failed to load template details';
    } finally {
      this.isLoading = false;
    }
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/logbook/chemicals/templates']);
  }

  editTemplate(): void {
    if (this.templateId) {
      this.router.navigate(['/logbook/chemicals/templates', this.templateId, 'edit']);
    }
  }

  duplicateTemplate(): void {
    // Placeholder - will be implemented later
    console.log('Duplicate functionality coming soon');
  }

  useTemplate(): void {
    // Placeholder - will navigate to spray record form with template pre-selected
    console.log('Use template functionality coming soon');
  }

  // Delete functionality
  confirmDelete(): void {
    if (this.template) {
      this.confirmModal.open({
        title: 'Delete Template',
        message: `Are you sure you want to delete "${this.template.name}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonClass: 'btn-danger',
        icon: 'bi-trash'
      });
    }
  }

  async onDeleteConfirmed(): Promise<void> {
    if (this.template) {
      const success = await this.chemicalTemplateService.deleteTemplate(this.template.id);
      if (success) {
        console.log('Template deleted successfully');
        this.router.navigate(['/logbook/chemicals/templates']);
      } else {
        this.errorMessage = 'Failed to delete template';
      }
    }
  }

  onDeleteCancelled(): void {
    // No action needed
  }

  // Format currency
  formatCurrency(amount: number | null | undefined): string {
    if (amount === undefined || amount === null) {
      return 'Not specified';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  // Source badge helpers
  getSourceBadgeClass(source?: string | null): string {
    switch (source) {
      case 'epa':
        return 'bg-primary-subtle text-primary';
      case 'adn-recommended':
        return 'bg-success-subtle text-success';
      case 'custom':
        return 'bg-warning-subtle text-warning-emphasis';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  getSourceLabel(source?: string | null): string {
    switch (source) {
      case 'epa':
        return 'EPA Registered';
      case 'adn-recommended':
        return 'Acre Advantage';
      case 'custom':
        return 'Custom';
      default:
        return 'Unknown';
    }
  }

  // TrackBy functions
  trackByIndex(index: number): number {
    return index;
  }
}
